import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push as navigateTo } from 'connected-react-router';
import { useTranslation } from 'react-i18next';
import { useParams } from 'util/reactRouterHooks';
import { State } from 'reducers';
import { AugmentedBuilding, Building } from 'shared/models';
import { callApi } from 'apiClient';
import { getBuildingByUrlSlug, HasUrlSlug } from 'selectors/buildingsSelectors';
import { BuildingDetails, BuildingForm } from '../buildingForm/BuildingForm';
import { updateBuildingSuccess } from 'actions/buildingActions';
import { omit } from 'lodash';
import { toError } from 'shared/util/toError';

export const BuildingAddressPage = () => {
    const { t } = useTranslation();
    const params = useParams<HasUrlSlug>();
    const building = useSelector((state: State) => getBuildingByUrlSlug(state, params));

    const [ busy, setBusy ] = useState(false);
    const [ errorMessage, setErrorMessage ] = useState('');
    const [ successMessage, setSuccessMessage ] = useState('');

    const dispatch = useDispatch();

    const handleSave = useCallback((details: BuildingDetails) => {
        setBusy(true);
        setErrorMessage('');
        setSuccessMessage('');
        void (async () => {
            try {
                // HACK: Some buildings are actually AugmentedBuilding, which is Building plus a bunch of other
                // stats and related data. And we don't want to transmit that stuff to the server when we update
                // a building.
                // TODO: get rid of AugmentedBuilding and store extra data in a different data store so that
                // building can be purely just Building. (and perhaps some of the data doesn't even need to
                // be kept in Redux...)
                const fieldsToRemove: (keyof AugmentedBuilding)[] = [
                    "hasMoreEvents",
                    "eventsLoading",
                    "eventsError",
                    "credentialsOriginal",
                    "broadcastMessage",
                    "previousBroadcastMessage",
                    "settingBroadcast",
                    "settingBroadcastError",
                    "savingCredentials",
                    "events",
                    "credentials",
                    "userIds",
                    "themeIds",
                    "devices",
                    "devicesDown",
                    "devicesLate1",
                    "devicesLate2",
                    "devicesOffline",
                    "devicesUp",
                    "devicePins",
                    // Other fields to exclude
                    "enabled",
                    "created",
                    "hasEcs",
                ];
                const building = omit(details, ...fieldsToRemove) as BuildingDetails;
                
                const updated: Building = await callApi('PUT', `/api/buildings/${building.id!}`, building);
                dispatch(updateBuildingSuccess(updated));
                setSuccessMessage(t('SAVED_MESSAGE'));
        
                // Redirect to the new building settings page (in case the url slug changed)
                if (building.urlSlug !== updated.urlSlug) {
                    dispatch(navigateTo(`/buildings/${updated.urlSlug}/settings`));
                }
            }
            catch (e) {
                const err = toError(e);
                const errorMessage = t([ `ERROR.${err.message}`, 'SERVER_ERROR' ]);
                setErrorMessage(errorMessage);
                return;
            }
            finally {
                setBusy(false);
            }
            await new Promise(resolve => setTimeout(resolve, 3000));
            setSuccessMessage('');
        })();
    }, [ dispatch, setBusy, setErrorMessage, setSuccessMessage, t ]);

    return (
        <div
            id="building-address-page"
            className="page"
        >
            <BuildingForm
                busy={ busy }
                building={ building }
                create={ false }
                errorMessage={ errorMessage }
                successMessage={ successMessage }
                onSave={ handleSave }
            />
        </div>
    );
};
